import { BracketMatchStatusPayload } from "@bracket/idl/main/bracket/model/BracketPt"

/** 대진표 목록 한 줄 (체급 선택 카드) */
interface BracketSummaryItem {
  id: string
  tournamentId: string
  sportLabel: string
  divisionLabel: string
  genderLabel: string
  weightClassName: string
  /** 대진 규모 표시 (예: "8강 토너먼트") */
  sizeLabel: string
  playerCount: number
  matchCount: number
  roundCount: number
  /** 공개 여부 — 편성은 곧 공개가 아니다 */
  isPublic: boolean
}

/** 경기에 배치된 선수 (빈 자리면 이름이 비어 있다) */
interface BracketMatchPlayerItem {
  playerId: string
  name: string
  gymName: string
  weightKg: string
}

interface BracketMatchItem {
  id: string
  roundNo: number
  matchNo: number
  /** 종목별 경기 번호 (종이 대진표의 「N경기」) — 종목마다 1부터다 */
  orderNo: number
  roundName: string
  homePlayer: BracketMatchPlayerItem
  awayPlayer: BracketMatchPlayerItem
  status: BracketMatchStatusPayload
  statusLabel: string
  /** 부전승 자리 — 붙을 상대가 없어 선만 이어진다 */
  isBye: boolean
}

interface BracketRound {
  roundNo: number
  roundName: string
  matches: BracketMatchItem[]
}

interface BracketDetail {
  id: string
  tournamentId: string
  sportLabel: string
  divisionLabel: string
  genderLabel: string
  weightClassName: string
  sizeLabel: string
  slotCount: number
  roundCount: number
  /** 공개 여부 — 관리자 화면이 토글로 쓴다 */
  isPublic: boolean
  rounds: BracketRound[]
}

/** 경기 순서 한 줄 (대회 전체 경기 목록) */
interface BracketMatchOrderItem {
  /** 종목별 경기 번호 — 종목마다 1부터다 */
  orderNo: number
  matchId: string
  bracketId: string
  sportLabel: string
  divisionLabel: string
  genderLabel: string
  weightClassName: string
  roundName: string
  homePlayer: BracketMatchPlayerItem
  awayPlayer: BracketMatchPlayerItem
  statusLabel: string
  isBye: boolean
}

/** 종목 하나로 묶은 목록 — 화면이 종목 제목을 달아 그린다 */
interface BracketSportGroup<T> {
  sportLabel: string
  items: T[]
}

/** 편성 대상 한 줄 (관리자) */
interface BracketDrawTargetItem {
  key: string
  sportLabel: string
  divisionLabel: string
  genderLabel: string
  weightClassName: string
  playerCount: number
  bracketId: string
  /** 편성 요청에 그대로 되돌려 보낼 값 */
  request: BracketDrawRequestInput
}

/** 편성 요청 입력값 (IDL enum 이름 문자열) */
interface BracketDrawRequestInput {
  tournamentId: string
  sport: string
  division: string
  gender: string
  weightClassName: string
}

const BRACKET_MATCH_STATUS_LABELS: Record<BracketMatchStatusPayload, string> = {
  [BracketMatchStatusPayload.BRACKET_MATCH_STATUS_UNKNOWN]: "알 수 없음",
  [BracketMatchStatusPayload.BRACKET_MATCH_SCHEDULED]: "예정",
  [BracketMatchStatusPayload.BRACKET_MATCH_BYE]: "부전승",
  [BracketMatchStatusPayload.UNRECOGNIZED]: "알 수 없음",
}

/** 편성 규모 선택지 — 서버 enum 이름과 같아야 한다 */
const BRACKET_SIZE_OPTIONS = [
  { value: "BRACKET_SIZE_AUTO", label: "자동 (인원에 맞춤)" },
  { value: "BRACKET_SIZE_QUARTER_FINAL", label: "8강" },
  { value: "BRACKET_SIZE_SEMI_FINAL", label: "4강" },
  { value: "BRACKET_SIZE_FINAL", label: "결승" },
]

/** 1라운드 슬롯 수별 대진 규모 표시 (종이 대진표의 「8강 토너먼트」) */
const BRACKET_SIZE_LABELS: Record<number, string> = {
  2: "결승",
  4: "4강 토너먼트",
  8: "8강 토너먼트",
  16: "16강 토너먼트",
  32: "32강 토너먼트",
}

function bracketMatchStatusLabel(status: BracketMatchStatusPayload): string {
  return BRACKET_MATCH_STATUS_LABELS[status] ?? "알 수 없음"
}

function bracketSizeLabel(slotCount: number): string {
  return BRACKET_SIZE_LABELS[slotCount] ?? `${slotCount}명 토너먼트`
}

export {
  BRACKET_MATCH_STATUS_LABELS,
  BRACKET_SIZE_LABELS,
  BRACKET_SIZE_OPTIONS,
  bracketMatchStatusLabel,
  bracketSizeLabel,
}
export type {
  BracketDetail,
  BracketDrawRequestInput,
  BracketDrawTargetItem,
  BracketMatchItem,
  BracketMatchOrderItem,
  BracketMatchPlayerItem,
  BracketRound,
  BracketSportGroup,
  BracketSummaryItem,
}
